// Ficha do contato (painel lateral): dados, status, minicursos e matrícula pelo painel.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import { Link } from 'react-router-dom';
import { FiEdit2, FiExternalLink, FiSend, FiTrash2, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../../config/firebaseConfig';
import { Medidor, SeloStatus } from '../relatorios/Graficos';
import ConversaWhatsApp from './ConversaWhatsApp';
import { useTotaisDeEtapas } from '../relatorios/useRelatorios';
import { statusDoAluno, telefoneDoProgresso, tempoRelativo } from '../relatorios/util';
import {
  STATUS_CONTATO, formatarDataSimples, formatarTelefoneContato, progressosDoContato, statusDoContato,
} from './contatosUtil';

const apiUrl = process.env.REACT_APP_API_URL;

export function SeloContato({ status }) {
  const s = statusDoContato(status);
  const Icone = s.icone;
  return (
    <span className={s.tom === 'neutral' ? 'qz-badge' : `qz-badge qz-badge--${s.tom}`} title={s.descricao}>
      {Icone && <Icone aria-hidden="true" />}
      {s.rotulo}
    </span>
  );
}

function ContatoFicha({ tenantId, contato, minicursos, progressos, onFechar, onEditar, onExcluir }) {
  const cursos = useMemo(
    () => progressosDoContato(contato, progressos)
      .sort((a, b) => (b.timestampUltimaEtapa?.seconds || 0) - (a.timestampUltimaEtapa?.seconds || 0)),
    [contato, progressos],
  );
  const totais = useTotaisDeEtapas(cursos.map((p) => p.minicursoId));
  const ativos = useMemo(() => minicursos.filter((m) => m.status === 'ativo'), [minicursos]);
  const emAndamento = cursos.find((p) => !p.concluido);
  const nomeDoCurso = (id) => minicursos.find((m) => m.id === id)?.nome || 'Curso removido';

  const [botConectado, setBotConectado] = useState(null);
  const [cursoEscolhido, setCursoEscolhido] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    axios.post(`${apiUrl}/bot-status`, { sanitizedClientId: tenantId })
      .then((r) => setBotConectado(r.data?.status === 'connected'))
      .catch(() => setBotConectado(false));
  }, [tenantId]);

  useEffect(() => { setConfirmando(false); }, [cursoEscolhido]);

  const mudarStatus = async (e) => {
    try {
      await updateDoc(doc(firestore, 'contatos', contato.id), { status: e.target.value, dataUltimaAtualizacao: new Date().toISOString() });
      toast.success(`Status alterado para ${e.target.value}.`);
    } catch (erro) {
      console.error('Erro ao alterar status:', erro);
      toast.error('Não foi possível alterar o status.');
    }
  };

  const matricular = async () => {
    setEnviando(true);
    try {
      await axios.post(`${apiUrl}/matricular`, {
        sanitizedClientId: tenantId, telefone: contato.telefone, minicursoId: cursoEscolhido,
        contatoId: contato.id, nome: contato.nome,
      });
      toast.success(`${contato.nome} matriculado(a) em ${nomeDoCurso(cursoEscolhido)}. A primeira etapa foi enviada.`);
      setCursoEscolhido('');
    } catch (erro) {
      toast.error(erro.response?.data?.error || 'Não foi possível matricular. Verifique se o bot está conectado.');
    } finally {
      setEnviando(false);
      setConfirmando(false);
    }
  };

  const vaiInterromper = emAndamento && cursoEscolhido && emAndamento.minicursoId !== cursoEscolhido;
  const vaiReiniciar = cursoEscolhido && cursos.some((p) => p.minicursoId === cursoEscolhido);

  const dados = [
    ['Telefone', formatarTelefoneContato(contato.telefone)],
    ['E-mail', contato.email || '—'],
    ['Nascimento', formatarDataSimples(contato.dataNascimento)],
    ['Gênero', contato.genero || '—'],
    ['Tipo', contato.tipoContato || '—'],
    ['Origem', contato.origem || '—'],
    ['Interesses', contato.interesses || '—'],
    ['Curso de origem', [contato.curso, contato.turma].filter(Boolean).join(' · ') || '—'],
    ['Cadastrado em', formatarDataSimples(contato.dataCriacao)],
  ];

  return (
    <Modal isOpen onRequestClose={onFechar} className="ct-ficha" overlayClassName="ct-overlay ct-overlay--lateral" contentLabel={`Ficha de ${contato.nome}`}>
      <header className="ct-ficha__topo">
        <div>
          <h2 className="ct-ficha__nome">{contato.nome || 'Sem nome'}</h2>
          <SeloContato status={contato.status} />
        </div>
        <button type="button" className="btn-ghost ct-icone-botao" onClick={onFechar} aria-label="Fechar ficha">
          <FiX aria-hidden="true" />
        </button>
      </header>

      <div className="ct-ficha__acoes">
        <button type="button" className="btn-secondary ct-botao" onClick={onEditar}><FiEdit2 aria-hidden="true" /> Editar</button>
        <button type="button" className="btn-danger ct-botao" onClick={onExcluir}><FiTrash2 aria-hidden="true" /> Excluir</button>
      </div>

      <section className="ct-ficha__secao">
        <h3>Dados</h3>
        <dl className="ct-dados">
          {dados.map(([rotulo, valor]) => (
            <div key={rotulo}><dt>{rotulo}</dt><dd>{valor}</dd></div>
          ))}
          {contato.linkedin && (
            <div><dt>LinkedIn</dt><dd><a href={contato.linkedin} target="_blank" rel="noreferrer">Abrir perfil <FiExternalLink aria-hidden="true" /></a></dd></div>
          )}
        </dl>
        <div className="ct-campo">
          <label htmlFor="ct-ficha-status">Status do contato</label>
          <select id="ct-ficha-status" value={contato.status || ''} onChange={mudarStatus}>
            {STATUS_CONTATO.map((s) => <option key={s.valor} value={s.valor}>{s.rotulo} · {s.descricao}</option>)}
          </select>
        </div>
      </section>

      <section className="ct-ficha__secao">
        <h3>Minicursos</h3>
        {cursos.length === 0 ? (
          <p className="ct-suave">Ainda não participou de nenhum minicurso.</p>
        ) : (
          <ul className="ct-cursos">
            {cursos.map((p) => (
              <li key={p.id} className="ct-curso">
                <div className="ct-curso__topo">
                  <strong>{nomeDoCurso(p.minicursoId)}</strong>
                  <SeloStatus status={statusDoAluno(p)} />
                </div>
                <Medidor valor={p.ultimaEtapaConcluida || 0} total={totais[p.minicursoId] || 0} />
                <span className="ct-suave">Acertos {p.acertos || 0} · erros {p.erros || 0} · última atividade {tempoRelativo(p.timestampUltimaEtapa)}</span>
              </li>
            ))}
          </ul>
        )}
        {cursos.length > 0 && (
          <Link className="ct-link" to={`/relatorios?aba=aluno&aluno=${telefoneDoProgresso(cursos[0])}`}>
            Ver todas as respostas no relatório <FiExternalLink aria-hidden="true" />
          </Link>
        )}
      </section>

      <section className="ct-ficha__secao">
        <h3>Conversa no WhatsApp</h3>
        <ConversaWhatsApp tenantId={tenantId} contato={contato} />
      </section>

      <section className="ct-ficha__secao">
        <h3>Matricular em minicurso</h3>
        <p className="ct-suave">O bot envia a boas-vindas e a primeira etapa na hora; as respostas do contato passam a valer para este curso.</p>
        {botConectado === false && (
          <p className="ct-aviso">
            O bot está desconectado. <Link to="/configuracao/bot">Conecte o WhatsApp</Link> para matricular.
          </p>
        )}
        <div className="ct-matricula">
          <select
            aria-label="Minicurso"
            value={cursoEscolhido}
            onChange={(e) => setCursoEscolhido(e.target.value)}
            disabled={!botConectado || enviando}
          >
            <option value="">Escolha um minicurso ativo</option>
            {ativos.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
          </select>
          {!confirmando ? (
            <button
              type="button"
              className="btn-primary ct-botao"
              disabled={!botConectado || !cursoEscolhido || enviando}
              onClick={() => (vaiInterromper ? setConfirmando(true) : matricular())}
            >
              <FiSend aria-hidden="true" /> {enviando ? 'Enviando...' : vaiReiniciar ? 'Reiniciar e enviar' : 'Matricular e enviar'}
            </button>
          ) : null}
        </div>
        {confirmando && (
          <div className="ct-confirmacao" role="alertdialog" aria-label="Confirmar troca de curso">
            <p>
              {contato.nome} está em <strong>{nomeDoCurso(emAndamento.minicursoId)}</strong> (etapa {emAndamento.ultimaEtapaConcluida || 0}).
              A matrícula vai <strong>interromper</strong> esse curso; o histórico continua nos relatórios.
            </p>
            <div className="ct-confirmacao__botoes">
              <button type="button" className="btn-secondary ct-botao" onClick={() => setConfirmando(false)}>Cancelar</button>
              <button type="button" className="btn-primary ct-botao" onClick={matricular} disabled={enviando}>
                {enviando ? 'Enviando...' : 'Interromper e matricular'}
              </button>
            </div>
          </div>
        )}
        {vaiReiniciar && !confirmando && (
          <p className="ct-suave">Já fez este curso: ele recomeça do zero (conta como nova tentativa).</p>
        )}
      </section>
    </Modal>
  );
}

export default ContatoFicha;
